import { EmptyImages } from "@utils/enums";
import { attemptMethod, hasWindow } from "@utils/general";

import FeatureToggles, {
    FeatureToggleKeys,
} from "@services/featureToggles.services";

type AuthenticationContext = {
    loginPageUrl: string;
    requestPasswordUrl: string;
    authenticationRequired: boolean;
};

export const BRAND_HUBO = "Hubo";

export function getCountry(): string {
    return attemptMethod(() => window.configuration?.country, "");
}

export function getLocale(getShortVersion: boolean = false): string {
    if (getShortVersion) {
        return attemptMethod(
            () => window.configuration?.locale?.substring(0, 2),
            "",
        );
    }
    return attemptMethod(() => window.configuration?.locale, "");
}

export function getApiBaseUrl(): string {
    return attemptMethod(() => window.configuration?.apiBaseUrl, "");
}

export function getWoosmapKey(): string {
    return attemptMethod(() => window.configuration?.woosmap?.key, "");
}

export function getAidenApiKey(): string {
    return attemptMethod(() => window.configuration?.aiden?.key, "");
}

export function getBFFApiBaseUrl(): string {
    return (
        attemptMethod(() => window.configuration?.bffApiBaseUrl, undefined) ||
        getApiBaseUrl()
    );
}

export function getPageTemplate(): string {
    return attemptMethod(() => window.configuration?.pageTemplate, "");
}

export function getEnvironment(): string {
    return attemptMethod(() => window.configuration?.environment, "");
}

export function isTestEnvironment(): boolean {
    return !!["local", "test"].find(env => env === getEnvironment());
}

export function getAuthenticationContext(): AuthenticationContext {
    return attemptMethod(() => window.configuration?.authenticationContext, "");
}

export function getBaseStore(): string {
    return attemptMethod(() => window.configuration?.baseStore, "");
}

export function isReviewsEnabled(): boolean {
    return (
        attemptMethod(() => window.configuration?.bazaarVoice.enabled, false) ||
        FeatureToggles.isEnabled(FeatureToggleKeys.REVIEWS)
    );
}

export function getReCaptchaSiteKey(): string {
    return attemptMethod(
        () => window.configuration?.googleCaptcha?.siteKey,
        "",
    );
}

export function getDamPath(): string {
    return attemptMethod(
        () => window.configuration?.damPath,
        "/content/dam/hubo",
    );
}

export function getCheckoutRedirectStep(stepAlias: string): string {
    return attemptMethod(
        () => window.configuration?.checkoutStep[stepAlias],
        "",
    );
}

export function getHomepage(): string {
    return attemptMethod(() => window.configuration?.homepage, "");
}

export function getMollieProfileId(): string {
    return attemptMethod(() => window.configuration?.mollie?.profileId, "");
}

export function isMollieTestMode(): boolean {
    return attemptMethod(() => !!window.configuration?.mollie?.testMode, true);
}

export function getGoogleMapsKey(): string {
    return attemptMethod(() => window.configuration?.googleMaps?.siteKey, "");
}

export function getGoogleMapsLat(): number {
    return attemptMethod(
        () => window.configuration?.googleMaps?.siteLat,
        50.5959938,
    );
}

export function getGoogleMapsLong(): number {
    return attemptMethod(
        () => window.configuration?.googleMaps?.siteLong,
        4.5212406,
    );
}

export function getGoogleMapsVersion(): string {
    return attemptMethod(() => window.configuration?.googleMaps?.version, "");
}

export function getGoogleMapsLibraries(): Array<string> {
    return attemptMethod(
        () => window.configuration?.googleMaps?.libraries,
        ["geometry, drawing, places"],
    );
}

export function getComparePageUrl(): string {
    return attemptMethod(() => window.configuration?.comparePageUrl, "");
}

export function getWishlistPageUrl(): string {
    return attemptMethod(() => window.configuration?.wishlistPageUrl, "");
}

export function getSharedWishlistPageUrl(): string {
    return attemptMethod(() => window.configuration?.sharedWishlistPageUrl, "");
}

export function getShoppingCartRedirect(): string {
    return attemptMethod(() => window.configuration?.shoppingCart, "");
}

export function getMaximumOrderQuantity(): number {
    return (
        attemptMethod(() => window.configuration?.maximumOrderQuantity, 9999) ||
        9999
    );
}

export function getDefaultImage(): string {
    return attemptMethod(
        () => window.configuration?.defaultImage,
        EmptyImages.Size_1_1,
    );
}

export function getFallbackImage(): string {
    return attemptMethod(
        () => window.configuration?.fallbackImage,
        EmptyImages.Size_146_109,
    );
}

export function getNeverbounceApiKey(): string {
    return attemptMethod(() => window.configuration?.neverbounce.apiKey, "");
}

export function getNeverbounceTimeout(): number {
    return attemptMethod(() => window.configuration?.neverbounce.timeout, 2000);
}

export function isNeverbounceEnabled(): boolean {
    return hasWindow() && !!window.configuration?.neverbounce.enabled;
}

export function getGiftCardPath(): string {
    return attemptMethod(() => window.configuration?.giftCardPath, "") || "";
}

export function getBpostApiKey(): string {
    return attemptMethod(() => window.configuration.bpost.apiKey, "");
}

export function getBpostUrlValidation(): string {
    return attemptMethod(() => window.configuration.bpost.urlValidation, "");
}

export function getBpostUrlAutocomplete(): string {
    return attemptMethod(() => window.configuration.bpost.urlAutocomplete, "");
}

export function getAlgoliaProductIndex(): string {
    return attemptMethod(() => window.configuration.algolia.productIndex, "");
}
